import {
  Box,
  Button,
  Checkbox,
  DateInput,
  Divider,
  Flex,
  FormikField,
  LegacyText,
  Stack,
  Text,
  toDateString,
} from "flicket-ui";
import {
  NamingFieldType,
  SignupFormFieldFragmentFragment,
  SignupFormStylesFragmentFragment,
} from "~graphql/typed-document-nodes";
import { Formik, FormikHelpers, FormikValues } from "formik";
import { genderOptions } from "~form/naming";
import { marketingOptInCopy } from "~config";
import { Select } from "~components";
import { Dispatch, SetStateAction } from "react";
import {
  initialFormValues,
  validationSchema,
} from "~features/embed/field.validation";
import { useOrganization, useSDK } from "~hooks";
import { showToast } from "~lib";
import { useRouter } from "next/router";
import { v4 as uuidv4 } from "uuid";
import { textColourFromLuminance } from "~lib/helpers/colours";
import { useTheme } from "styled-components";

export interface SignupEmbedFormInterface {
  id?: string;
  heading: string;
  description: string;
  buttonText: string;
  styles: SignupFormStylesFragmentFragment;
  formFields?: SignupFormFieldFragmentFragment[];
  successMessage: string;
  successHeading: string;
  processedCustomCSS: string;
}

interface SignupEmbedFormProps extends SignupEmbedFormInterface {
  setModalState: Dispatch<SetStateAction<"form" | "success">>;
}

export default function SignupEmbedForm(props: SignupEmbedFormProps) {
  const {
    heading,
    description,
    buttonText,
    styles,
    formFields,
    setModalState,
  } = props;

  const { organization } = useOrganization();
  const sdk = useSDK();
  const router = useRouter();
  const isPreview = router.query.preview === "true";
  const theme = useTheme();

  const submit = async (
    values: FormikValues,
    actions: FormikHelpers<FormikValues>
  ) => {
    if (isPreview) {
      actions.resetForm();
      setModalState("success");
      return;
    }

    actions.setSubmitting(true);
    const fieldValues = formFields
      .map((item) => {
        const isCustomField = [
          NamingFieldType.CustomText,
          NamingFieldType.CustomSelect,
          NamingFieldType.CustomCheckbox,
        ].includes(item.type);

        const key = isCustomField ? item.name : item.type;

        // handle checkbox
        if (values[key] === true) {
          values[key] = "true";
        }
        if (values[key] === false) {
          values[key] = "false";
        }

        // handle date
        if (item.type === NamingFieldType.BirthDate) {
          values[key] = toDateString(values[key]);
        }

        // handle select
        if (typeof values[key] === "object" && "value" in values[key]) {
          values[key] = values[key].value;
        }

        if (values[key]) {
          return {
            signUpFormFieldId: item.id,
            label: item.label,
            value: values[key],
            type: item.type,
          };
        }
      })
      .filter(Boolean);

    const result = await sdk.SignupFormSubmit({
      input: {
        signUpFormId: props.id,
        fieldValues,
      },
    });
    if (result.signupFormSubmit) {
      actions.resetForm();
      setModalState("success");
      return;
    }

    actions.setSubmitting(false);
    return showToast("Subscribe failed", "error");
  };

  return (
    <Stack
      id="flicket-form"
      direction="vertical"
      width="100%"
      backgroundColor={styles.backgroundColour as any}
      height="100vh"
    >
      <Formik
        initialValues={initialFormValues(formFields)}
        validationSchema={validationSchema({
          signupFormFields: formFields,
        })}
        onSubmit={submit}
        enableReinitialize
      >
        {({
          handleSubmit,
          setFieldValue,
          isSubmitting,
          values,
          errors,
          touched,
        }) => (
          <Flex
            flexDir="column"
            as="form"
            onSubmit={handleSubmit}
            height="100%"
          >
            <Stack direction={"vertical"}>
              <Box p={4} pb={0}>
                {isPreview && <Text variant="header.Minor">PREVIEW</Text>}
                <Text
                  variant="header.M"
                  as="h2"
                  color={styles.textColour as any}
                  className="flicket-text"
                >
                  {heading}
                </Text>
              </Box>

              <Divider
                mt={3}
                id="flicket-form-divider"
                className="flicket-divider"
              />
            </Stack>
            <Stack
              flexWrap="wrap"
              gap={3}
              overflowY="auto"
              px={4}
              py={3}
              flex={1}
              alignContent="flex-start"
            >
              {description && (
                <Text
                  as="p"
                  variant="regular"
                  color={styles.textColour as any}
                  className="flicket-text"
                >
                  {description}
                </Text>
              )}
              {formFields?.map(({ type, options, name }, index) => {
                const namingField = formFields[index];
                const isCustomField = [
                  NamingFieldType.CustomText,
                  NamingFieldType.CustomSelect,
                  NamingFieldType.CustomCheckbox,
                ].includes(namingField.type);

                const label = namingField.label;

                // value from preview don't have name, use label instead
                const fieldName = isCustomField ? name ?? label : type;

                const isCheckbox = [
                  NamingFieldType.CustomCheckbox,
                  NamingFieldType.UsableForSmsMarketing,
                  NamingFieldType.UsableForEmailMarketing,
                ].includes(namingField.type);

                let fieldProps: { name: string; label: string } & Record<
                  string,
                  any
                > = {
                  name: fieldName,
                  label,
                  as: undefined,
                };

                if (isCheckbox) {
                  fieldProps = {
                    ...fieldProps,
                    as: Checkbox,
                    checked: values[fieldName] ?? false,
                    theme: {
                      ...theme,
                      colors: { ...theme.colors, P300: styles.textColour },
                    },
                  };
                }

                if (type === NamingFieldType.Gender) {
                  fieldProps = {
                    ...fieldProps,
                    as: Select,
                    fieldProps: {
                      ...fieldProps?.fieldProps,
                      options: genderOptions,
                      onChange: (val: { label: string; value: string }) => {
                        setFieldValue(fieldName, val);
                      },
                    },
                  };
                }

                if (type === NamingFieldType.BirthDate) {
                  fieldProps = {
                    ...fieldProps,
                    ...fieldProps?.fieldProps,
                    as: DateInput,
                    onChange: (date: string) => setFieldValue(fieldName, date),
                  };
                }
                if (type === NamingFieldType.UsableForEmailMarketing) {
                  fieldProps.label = marketingOptInCopy(organization, "email");
                  fieldProps = Object.assign(fieldProps, {
                    checked: !!organization?.defaultOptInMarketing,
                  });
                }
                if (type === NamingFieldType.UsableForSmsMarketing) {
                  fieldProps.label = marketingOptInCopy(organization, "sms");
                  fieldProps = Object.assign(fieldProps, {
                    checked: !!organization?.defaultOptInMarketing,
                  });
                }

                if (type === NamingFieldType.CustomSelect) {
                  const customOptions = (options ?? []).map((o) => ({
                    value: o,
                    label: o,
                  }));

                  fieldProps = {
                    ...fieldProps,
                    as: Select,
                    fieldProps: {
                      ...fieldProps?.fieldProps,
                      options: customOptions,
                      onChange: (val: { label: string; value: string }) => {
                        setFieldValue(fieldName, val);
                      },
                    },
                  };
                }

                return (
                  <Stack
                    key={fieldProps.name}
                    direction={"vertical"}
                    width="100%"
                    gap={"1/2"}
                  >
                    <FormikField
                      className={`flicket-form-input ${
                        isCheckbox ? "flicket-form-checkbox" : ""
                      }`}
                      id={`flicket-form-input-${uuidv4()}`}
                      flexBasis={"100%"}
                      {...fieldProps}
                      label={
                        ((
                          <LegacyText
                            color={styles.textColour as any}
                            className={"flicket-text flicket-form-label"}
                            id={`flicket-form-label-${uuidv4()}`}
                          >
                            {fieldProps.label}
                            <Text
                              as="span"
                              variant="small"
                              color={styles.textColour as any}
                              opacity={0.7}
                            >
                              {namingField?.required ? "*" : " (Optional)"}
                            </Text>
                          </LegacyText>
                        ) as unknown) as string
                      }
                      hideError
                    />
                    {errors[fieldName] && touched[fieldName] && (
                      <Text
                        variant="small"
                        color="error100"
                        className={"flicket-text flicket-form-error"}
                        id={`flicket-form-error-${uuidv4()}`}
                      >
                        {errors[fieldName]}
                      </Text>
                    )}
                  </Stack>
                );
              })}
            </Stack>
            <Box width="100%">
              <Divider className="flicket-divider" />
              <Box px={3}>
                <Button
                  variant="primary"
                  type={"submit"}
                  isLoading={isSubmitting}
                  backgroundColor={`${styles.buttonColour}` as any}
                  className={"flicket-text flicket-form-button"}
                  id={`flicket-form-button`}
                  width="100%"
                  my={2}
                  color={textColourFromLuminance(styles.buttonColour) as any}
                >
                  {buttonText}
                </Button>
              </Box>
            </Box>
          </Flex>
        )}
      </Formik>
    </Stack>
  );
}
